import { Controller } from "@hotwired/stimulus"
import * as geometry from "./shared/chart_geometry"
import * as fmt from "./shared/chart_format"
import { tween } from "./shared/tween"
import { placeCard } from "./shared/tooltip_placement"
import { ruiLog } from "./shared/diagnostics"
import * as strip from "./shared/chart_timeline_strip"
import * as readout from "./shared/chart_readout"

// Same class as Chart::Styles::PHONE_HIDDEN (already in the host CSS).
const PHONE_HIDDEN = "max-[480px]:hidden"

/**
 * Chart Controller
 * @gem rapid_rails_ui
 * @version 0.84.0
 *
 * Interaction only. The chart is server-rendered, so it prints and works with JS off.
 * - Tooltip on hover (follows the pointer) and keyboard focus (anchored above the mark);
 *   tooltips: false turns the card off
 * - Escape hides the tooltip; arrow keys move between visible marks
 * - Click or Enter dispatches rui-chart:select (cancelable), then follows href unless prevented
 * - Hovering a mark dims other categories; hovering a legend item dims other series
 * - Legend click toggles a series (aria-pressed). The last visible series cannot be hidden.
 *   The axis and bars rescale; stacked totals recompute.
 * - Entry motion once on first view (animate: true): bars grow, lines draw in, areas fade up,
 *   slices reveal in order, projection and band fade in last
 * - Timeline (timeline:): play/pause button and scrubber reveal categories one by one;
 *   dispatches rui-chart:timeline { index, label, playing }
 * - Update API: dispatch rui-chart:update on the figure with { categories?, series } or
 *   { data } and marks tween to the new values; dispatches rui-chart:updated when done
 * - Timeline strip (type: :timeline_strip): bars grow from their start on entry; update
 *   takes { rows: [{ label, segments: [{ group?, to? }] }] } and moves ends and colours
 * - Crosshair (crosshair: true on line and area): a vertical guide snaps to the nearest x
 *   under the pointer and one card lists every series at that x; arrow keys move it too
 * - prefers-reduced-motion: no motion, final state shown; controls still work
 *
 * Markup comes from rui_chart. Marks carry data-chart-category, data-chart-series,
 * data-chart-value, data-chart-orientation, and data-chart-tooltip (JSON).
 */
const GROW_IN_TYPES = ["bar", "hbar", "stacked"]
const LINE_TYPES = ["line", "area"]
const RING_TYPES = ["donut", "pie"]
const MOTION_MS = 500
const UPDATE_MS = 400
const DIM = "var(--rui-chart-dim-opacity, 0.4)"

export default class extends Controller {
  static targets = ["mark", "tooltip", "scrubber", "playButton", "timelineLabel", "clip", "plot", "points", "finale", "crosshair", "plotHit", "caption", "readout", "readoutValue"]
  static values = {
    animate: { type: Boolean, default: true },
    tooltips: { type: Boolean, default: true },
    crosshair: { type: Boolean, default: false },
    readoutMode: { type: String, default: "cumulative" },
    readoutSeries: { type: Number, default: -1 },
    timelineStep: { type: Number, default: 600 },
    timelineAutoplay: { type: Boolean, default: false }
  }

  connect() {
    this.preflight()
    this.hiddenSeries = new Set()
    this.markTargets.forEach((mark) => { mark.dataset.chartBaseOpacity = mark.style.opacity || "" })
    this.capturePrototypes()
    this.syncFocusable()
    this.onResize = () => this.syncFocusable()
    window.addEventListener("resize", this.onResize)

    this.playing = false
    this.timelineIndex = this.categories.length - 1

    if (this.hasScrubberTarget && this.timelineAutoplayValue && !this.reducedMotion) {
      this.play()
    } else if (this.animateValue && !this.reducedMotion) {
      this.startEntry()
    }
  }

  disconnect() {
    this.observer?.disconnect()
    this.pause()
    this.cancelTween()
    window.removeEventListener("resize", this.onResize)
  }

  // Marks in a hidden variant (for example the horizontal set on a wide screen) must
  // leave the tab order. Only visible marks are focusable.
  syncFocusable() {
    this.markTargets.forEach((mark) => {
      mark.tabIndex = this.isVisible(mark) ? 0 : -1
    })
  }

  // ---------------------------------------------------------------- tooltip

  show(event) {
    const mark = event.currentTarget
    if (!this.hasTooltipTarget || !this.isVisible(mark)) return

    // Donut and pie slices pull out 4 px along their bisector on hover.
    if (mark.dataset.chartDx !== undefined) {
      mark.style.transform = `translate(${mark.dataset.chartDx}px, ${mark.dataset.chartDy}px)`
    }

    this.dimOthers(mark.dataset.chartCategory)
    if (this.crosshairValue && mark.dataset.chartOrientation === "line") {
      this.crosshairAt(Number(mark.dataset.chartCategory))
      return
    }
    if (!this.tooltipsValue) return

    this.renderTooltip(mark)

    if (event.type === "mouseenter") {
      this.placeAtPointer(event)
    } else {
      this.placeAboveMark(mark)
    }
  }

  hide() {
    this.markTargets.forEach((mark) => {
      if (mark.dataset.chartDx !== undefined) mark.style.transform = ""
    })
    if (this.hasTooltipTarget) this.tooltipTarget.hidden = true
    if (this.hasCrosshairTarget) this.crosshairTarget.style.opacity = "0"
    this.restoreOpacity()
  }

  // ---------------------------------------------------------------- crosshair (P6)

  // Pointer over the plot: snap to the nearest category and show one card for every
  // series at that x. Keyboard reaches the same through a focused point (show()).
  crosshairMove(event) {
    const geo = this.lineGeometry
    if (!geo) return
    const svg = this.element.querySelector("svg[data-chart-left]")
    const rect = svg.getBoundingClientRect()
    const x = geometry.unitsAtX(event.clientX - rect.left, rect.width, geo)
    const n = this.categories.length
    const step = n > 1 ? (geo.right - geo.left) / (n - 1) : 1
    const c = Math.max(0, Math.min(n - 1, Math.round((x - geo.left) / step)))
    this.crosshairAt(c)
  }

  crosshairLeave() {
    this.hide()
  }

  crosshairAt(c) {
    const geo = this.lineGeometry
    if (!geo || !this.hasCrosshairTarget) return
    const x = geo.x(c)
    this.crosshairTarget.setAttribute("x1", x.toFixed(2))
    this.crosshairTarget.setAttribute("x2", x.toFixed(2))
    this.crosshairTarget.style.opacity = ""
    this.dimOthers(String(c))

    if (!this.hasTooltipTarget) return
    const lines = this.seriesNames.map((name, s) => {
      const point = this.markFor(c, s)
      const payload = point ? JSON.parse(point.dataset.chartTooltip || "{}") : {}
      return { name, value: this.valueText(this.valueFor(c, s)), color: payload.lines?.[0]?.color }
    })
    const box = this.tooltipTarget
    box.replaceChildren()
    box.style.borderRadius = "var(--rui-chart-radius, 4px)"
    box.append(this.textNode("div", this.categories[c] ?? "", "font-semibold"))
    for (const line of lines) {
      const row = document.createElement("div")
      if (line.color) {
        const swatch = document.createElement("span")
        swatch.className = "inline-block size-2 mr-1 align-middle"
        swatch.style.backgroundColor = line.color
        swatch.style.borderRadius = "var(--rui-chart-radius, 4px)"
        row.append(swatch)
      }
      row.append(document.createTextNode(`${line.name}: ${line.value}`))
      box.append(row)
    }
    box.hidden = false

    const anchor = this.markFor(c, 0)
    if (anchor) {
      this.placeAboveMark(anchor)
    } else {
      const svg = this.element.querySelector("svg[data-chart-left]")
      const rect = svg.getBoundingClientRect()
      const figure = this.element.getBoundingClientRect()
      const px = rect.left - figure.left + geometry.plotPercent(x, 0, geo).left / 100 * rect.width
      const top = rect.top - figure.top + geo.top * (rect.height / Number(svg.getAttribute("viewBox").split(" ")[3]))
      this.placeTooltip({ x: px, top, bottom: top })
    }
  }

  renderTooltip(mark) {
    const payload = JSON.parse(mark.dataset.chartTooltip || "{}")
    const box = this.tooltipTarget
    box.replaceChildren()
    box.style.borderRadius = "var(--rui-chart-radius, 4px)"

    if (payload.text) {
      box.append(this.textNode("div", payload.text))
    } else {
      box.append(this.textNode("div", payload.title || "", "font-semibold"))
      for (const line of payload.lines || []) {
        const row = document.createElement("div")
        if (line.color) {
          const swatch = document.createElement("span")
          swatch.className = "inline-block size-2 mr-1 align-middle"
          swatch.style.backgroundColor = line.color
          swatch.style.borderRadius = "var(--rui-chart-radius, 4px)"
          row.append(swatch)
        }
        const label = [line.name, line.value].filter(Boolean).join(": ")
        row.append(document.createTextNode(label))
        if (line.share) row.append(this.textNode("span", ` · ${line.share}`, "opacity-80"))
        box.append(row)
      }
    }
    box.hidden = false
  }

  placeAtPointer(event) {
    const figure = this.element.getBoundingClientRect()
    const x = event.clientX - figure.left
    const y = event.clientY - figure.top
    this.placeTooltip({ x, top: y, bottom: y })
  }

  placeAboveMark(mark) {
    const markRect = mark.getBoundingClientRect()
    const figure = this.element.getBoundingClientRect()
    this.placeTooltip({
      x: markRect.left - figure.left + markRect.width / 2,
      top: markRect.top - figure.top,
      bottom: markRect.bottom - figure.top
    })
  }

  // Card placement, in this order:
  // Horizontal: centred on the anchor; if that would cross the plot's left edge
  // (the y-axis labels) it sits to the right of the anchor, and to the left near
  // the right edge. Always clamped inside the figure.
  // Vertical: above the anchor; else below it when that does not land on the
  // legend; else beside the anchor, centred on it (a 100% bar between a caption
  // and a legend has room on neither side); else above the figure.
  // A card wider than the figure (long labels in a narrow KPI card) gets the
  // figure's exact width and wraps, so the clamp keeps it inside and an
  // overflow-hidden card no longer cuts the text. Exact, not max-width: an
  // absolute card would shrink-to-fit into whatever room is left.
  placeTooltip({ x, top, bottom }) {
    const tip = this.tooltipTarget
    tip.style.transform = "none"
    tip.style.left = "0px"
    tip.style.top = "0px"
    tip.style.width = ""
    tip.style.whiteSpace = ""
    if (tip.offsetWidth > this.element.clientWidth) {
      tip.style.width = `${this.element.clientWidth}px`
      tip.style.whiteSpace = "normal"
    }

    const figureRect = this.element.getBoundingClientRect()
    const relative = (el) => {
      const r = el.getBoundingClientRect()
      return { left: r.left - figureRect.left, right: r.right - figureRect.left, top: r.top - figureRect.top, bottom: r.bottom - figureRect.top, width: r.width }
    }
    const caption = this.element.querySelector("figcaption")
    const legend = this.element.querySelector("[data-chart-legend]")
    const plot = this.element.querySelector("[data-chart-plot]")
    const figureWidth = this.element.clientWidth
    const plotBox = plot ? relative(plot) : { left: 0, right: figureWidth, width: figureWidth }
    const inset = plot ? Number(plot.dataset.chartPlotInset || 0) / 100 * plotBox.width : 0

    const { left, top: y } = placeCard({ x, top, bottom }, {
      width: tip.offsetWidth,
      height: tip.offsetHeight,
      figureWidth,
      figureHeight: this.element.clientHeight,
      captionBottom: caption ? relative(caption).bottom : 0,
      legendBox: legend ? relative(legend) : null,
      plotLeft: plotBox.left + inset,
      plotRight: plotBox.right
    })
    tip.style.left = `${left}px`
    tip.style.top = `${y}px`
  }

  // ---------------------------------------------------------------- keyboard and select

  keydown(event) {
    if (event.key === "Escape") {
      this.hide()
      return
    }

    if (event.key === "Enter" && event.currentTarget.tagName !== "A") {
      event.preventDefault()
      this.select(event)
      return
    }

    // Calendar: columns are weeks and rows are weekdays, so Left/Right move a
    // week and Up/Down a day, within the grid that is showing.
    if (event.currentTarget.dataset.chartOrientation === "day") {
      const days = { ArrowRight: 7, ArrowLeft: -7, ArrowDown: 1, ArrowUp: -1 }[event.key]
      if (!days) return
      event.preventDefault()
      const target = strip.isoOf(strip.dayOf(event.currentTarget.dataset.chartDate) + days)
      this.visibleMarks().find((m) => m.dataset.chartDate === target)?.focus()
      return
    }

    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key]
    if (!step) return

    event.preventDefault()
    const marks = this.visibleMarks()
    const index = marks.indexOf(event.currentTarget)
    const next = marks[(index + step + marks.length) % marks.length]
    next?.focus()
  }

  // The browser follows a mark's link itself, so target, download and Ctrl/Cmd or
  // middle click behave like any link. Only a canceled rui-chart:select stops it
  // (0.77.1: it used to cancel every click and navigate by script).
  select(event) {
    const mark = event.currentTarget
    const detail = {
      series: this.seriesNames[Number(mark.dataset.chartSeries)],
      label: this.categories[Number(mark.dataset.chartCategory)],
      value: Number(mark.dataset.chartValue),
      index: Number(mark.dataset.chartCategory)
    }
    if (mark.dataset.chartDate) detail.date = mark.dataset.chartDate

    const allowed = this.element.dispatchEvent(
      new CustomEvent("rui-chart:select", { detail, cancelable: true, bubbles: true })
    )
    if (!allowed && event.type === "click") event.preventDefault()
  }

  // ---------------------------------------------------------------- legend

  toggleSeries(event) {
    const button = event.currentTarget
    const index = Number(button.dataset.chartSeries)
    const visibleCount = this.seriesNames.length - this.hiddenSeries.size

    if (!this.hiddenSeries.has(index) && visibleCount <= 1) return

    if (this.hiddenSeries.has(index)) {
      this.hiddenSeries.delete(index)
    } else {
      this.hiddenSeries.add(index)
    }

    button.setAttribute("aria-pressed", String(!this.hiddenSeries.has(index)))
    this.rescale()
  }

  previewSeries(event) {
    const index = Number(event.currentTarget.dataset.chartSeries)
    this.markTargets.forEach((mark) => {
      mark.style.opacity = Number(mark.dataset.chartSeries) === index ? mark.dataset.chartBaseOpacity : DIM
    })
  }

  clearPreview() {
    this.restoreOpacity()
  }

  // ---------------------------------------------------------------- rescale (bars)

  // Recompute domain, ticks, bars, and grid for the visible series.
  rescale() {
    // Column hover areas and stack totals describe every series, so they hide while
    // a series is toggled off.
    const count = this.categories.length
    this.element.querySelectorAll("[data-chart-column], [data-chart-stack-total]").forEach((el) => {
      const c = Number(el.dataset.chartStackTotal ?? el.dataset.chartCategory)
      el.hidden = this.hiddenSeries.size > 0 || c >= count
    })
    const visible = this.seriesNames.map((_, i) => i).filter((i) => !this.hiddenSeries.has(i))
    const bands = this.bandsFor(visible, this.stacked)

    const yMax = this.element.dataset.chartYMax ? Number(this.element.dataset.chartYMax) : null
    let lo = 0
    let hi = 0
    for (const band of bands.values()) {
      for (const b of band) {
        lo = Math.min(lo, b.lo)
        hi = Math.max(hi, b.hi)
      }
    }
    if (yMax !== null) hi = Math.max(hi, yMax)
    if (hi === 0) hi = 1

    const ticks = this.niceTicks(lo, hi)
    const min = ticks[0]
    const max = ticks[ticks.length - 1]
    const range = max - min || 1

    this.markTargets.forEach((mark) => {
      const c = Number(mark.dataset.chartCategory)
      const s = Number(mark.dataset.chartSeries)
      const band = (bands.get(c) || []).find((b) => b.s === s)
      mark.style.transition = this.reducedMotion ? "none" : "top 300ms ease-out, height 300ms ease-out, left 300ms ease-out, width 300ms ease-out"
      if (!band || this.hiddenSeries.has(s)) {
        mark.hidden = true
        return
      }
      mark.hidden = false
      if (mark.dataset.chartOrientation === "vertical") {
        mark.style.top = `${((max - band.hi) / range) * 100}%`
        mark.style.height = `${((band.hi - band.lo) / range) * 100}%`
      } else {
        mark.style.left = `${((band.lo - min) / range) * 100}%`
        mark.style.width = `${((band.hi - band.lo) / range) * 100}%`
      }
    })

    this.redrawAxis(ticks, min, max, range)
  }

  get stacked() {
    return this.element.dataset.chartLayout === "stacked" && this.seriesNames.length > 1
  }

  // Map category -> [{s, lo, hi}] for the visible series
  bandsFor(visible, stacked) {
    const result = new Map()
    for (let c = 0; c < this.categories.length; c++) {
      let pos = 0
      let neg = 0
      const bands = []
      for (const s of visible) {
        const v = this.valueFor(c, s)
        if (stacked) {
          if (v >= 0) {
            bands.push({ s, lo: pos, hi: pos + v })
            pos += v
          } else {
            bands.push({ s, lo: neg + v, hi: neg })
            neg += v
          }
        } else {
          bands.push({ s, lo: Math.min(0, v), hi: Math.max(0, v) })
        }
      }
      result.set(c, bands)
    }
    return result
  }

  markFor(c, s) {
    return this.markTargets.find((m) => Number(m.dataset.chartCategory) === c && Number(m.dataset.chartSeries) === s)
  }

  valueFor(c, s) {
    const mark = this.markFor(c, s)
    return mark ? Number(mark.dataset.chartValue) : 0
  }

  redrawAxis(ticks, min, max, range) {
    for (const orientation of ["vertical", "horizontal"]) {
      const tickProto = this.prototypes[`tick-${orientation}`]
      const gridProto = this.prototypes[`grid-${orientation}`]
      const tickContainer = this.containers[`tick-${orientation}`]
      const gridContainer = this.containers[`grid-${orientation}`]

      this.element.querySelectorAll(`[data-chart-axis-tick][data-chart-orientation="${orientation}"]`).forEach((el) => el.remove())
      this.element.querySelectorAll(`[data-chart-grid][data-chart-orientation="${orientation}"]`).forEach((el) => el.remove())

      ticks.forEach((v, i) => {
        const position = ((v - min) / range) * 100
        if (tickProto && tickContainer) {
          const el = tickProto.cloneNode(true)
          el.dataset.chartTick = String(v)
          el.dataset.chartAxisTick = String(v)
          el.textContent = this.formatTick(v)
          if (orientation === "vertical") {
            el.style.top = `${100 - position}%`
          } else {
            el.style.left = `${position}%`
            // Re-thin for phones on the new tick count (server does it on first render).
            el.classList.toggle(PHONE_HIDDEN, geometry.phoneTickHidden(i, ticks.length))
          }
          tickContainer.append(el)
        }
        if (gridProto && gridContainer) {
          const grid = gridProto.cloneNode(true)
          grid.dataset.chartGrid = String(v)
          // Each clone gets its own position on its axis and clears the other.
          grid.style.top = orientation === "vertical" ? `${100 - position}%` : ""
          grid.style.left = orientation === "vertical" ? "" : `${position}%`
          gridContainer.append(grid)
        }
      })
    }
  }

  // Clone one tick and one grid line per orientation, and remember where they live.
  capturePrototypes() {
    this.prototypes = {}
    this.containers = {}
    for (const orientation of ["vertical", "horizontal"]) {
      for (const kind of ["tick", "grid"]) {
        const selector = kind === "tick" ? "[data-chart-axis-tick]" : "[data-chart-grid]"
        const original = this.element.querySelector(`${selector}[data-chart-orientation="${orientation}"]`)
        this.prototypes[`${kind}-${orientation}`] = original?.cloneNode(true)
        this.containers[`${kind}-${orientation}`] = original?.parentElement
      }
    }
  }

  niceTicks(lo, hi, target = 5) {
    return geometry.niceTicks(lo, hi, target)
  }

  // ---------------------------------------------------------------- formatting

  get format() {
    return fmt.resolveFormat(this.element.dataset.chartFormat)
  }

  formatTick(v) {
    return this.formatValue(v)
  }

  formatValue(v) {
    return fmt.formatValue(v, this.format)
  }

  // Value with unit, as the server renders it: the unit only applies to the default format.
  valueText(v) {
    return fmt.valueText(v, this.element.dataset.chartFormat, this.element.dataset.chartUnit)
  }

  plain(n) {
    return fmt.plain(n)
  }

  compact(v) {
    return fmt.compact(v)
  }

  shareText(v, base) {
    return fmt.shareText(v, base, this.format)
  }

  // ---------------------------------------------------------------- update API (P3)

  // rui-chart:update { categories?, series: [{ name, values }] } or { data: [{ label, value }] }.
  // Same series count and names as rendered. Marks tween to the new values; tooltips,
  // aria-labels, value labels, stack totals, legend values and the hidden table are
  // rewritten with the built-in formats. A playing timeline is paused first.
  update(event) {
    const detail = event.detail || {}
    if (["lollipop", "funnel", "calendar", "waffle", "slope"].includes(this.element.dataset.chartType)) {
      ruiLog("chart", `rui-chart:update is not supported on type: :${this.element.dataset.chartType} yet; re-render the chart instead`, { element: this.element })
      return
    }
    if (this.element.dataset.chartType === "timeline_strip") {
      this.pause()
      this.cancelTween()
      this.settleEntry()
      this.hide()
      this.updateStrip(detail)
      return
    }
    const series = detail.series || (detail.data ? [{ name: this.seriesNames[0], values: detail.data.map((r) => Number(r.value)) }] : null)
    if (!series) return

    if (series.length !== this.seriesNames.length) {
      console.warn("rui-chart:update ignored: series count must stay", this.seriesNames.length)
      return
    }
    const categories = detail.categories || (detail.data ? detail.data.map((r) => String(r.label)) : this.categories)
    const values = series.map((s) => Array.from({ length: categories.length }, (_, c) => Number(s.values?.[c] ?? 0)))

    this.pause()
    this.cancelTween()
    this.settleEntry()
    this.hide()

    const sameShape = categories.length === this.categories.length
    if (!sameShape) {
      // Different category count: the server markup cannot be stretched, so the
      // values swap without motion. Tooltips and tables still update.
      console.warn("rui-chart:update: category count changed; values swapped without motion")
    }
    this.element.dataset.chartCategories = JSON.stringify(categories)

    const type = this.element.dataset.chartType
    const finish = () => {
      this.writeValues(values, categories)
      this.rewriteText(values, categories)
      this.element.dispatchEvent(new CustomEvent("rui-chart:updated", { bubbles: true, detail: { categories, series } }))
    }

    if (type === "heatmap") {
      this.updateHeat(series, categories)
      this.element.dispatchEvent(new CustomEvent("rui-chart:updated", { bubbles: true, detail: { categories, series } }))
    } else if (type === "sparkline") {
      this.updateSpark(values[0], categories)
      this.element.dispatchEvent(new CustomEvent("rui-chart:updated", { bubbles: true, detail: { categories, series } }))
    } else if (RING_TYPES.includes(type)) {
      this.updateRing(values[0], sameShape && !this.reducedMotion, finish)
    } else if (LINE_TYPES.includes(type)) {
      this.updateLine(values, sameShape && !this.reducedMotion, finish)
    } else {
      this.writeValues(values, categories)
      this.rescale()
      this.rewriteText(values, categories)
      this.element.dispatchEvent(new CustomEvent("rui-chart:updated", { bubbles: true, detail: { categories, series } }))
    }
  }

  writeValues(values, categories) {
    this.markTargets.forEach((mark) => {
      const c = Number(mark.dataset.chartCategory)
      const s = Number(mark.dataset.chartSeries)
      if (values[s] && c < values[s].length) mark.dataset.chartValue = String(values[s][c])
    })
    this.element.querySelectorAll("[data-chart-column]").forEach((col) => {
      const c = Number(col.dataset.chartCategory)
      col.dataset.chartValue = String(values.reduce((sum, v) => sum + Math.abs(v[c] ?? 0), 0))
    })
    void categories
  }

  // Rewrite every piece of text that carries a value.
  rewriteText(values, categories) {
    const seriesForm = this.element.dataset.chartSeriesForm === "true"
    const type = this.element.dataset.chartType
    const totals = categories.map((_, c) => values.reduce((sum, v) => sum + Math.abs(v[c] ?? 0), 0))
    const grand = values[0] ? values[0].reduce((a, b) => a + Math.abs(b), 0) : 0

    this.markTargets.forEach((mark) => {
      const c = Number(mark.dataset.chartCategory)
      const s = Number(mark.dataset.chartSeries)
      const v = values[s]?.[c] ?? 0
      const payload = JSON.parse(mark.dataset.chartTooltip || "{}")
      if (!payload.text) {
        if (RING_TYPES.includes(type)) {
          const pct = grand === 0 ? 0 : Math.round(Math.abs(v) / grand * 1000) / 10
          payload.title = categories[c]
          payload.lines = [{ ...(payload.lines?.[0] || {}), value: `${this.valueText(v)} · ${pct}%` }]
          mark.setAttribute("aria-label", `${categories[c]}: ${this.formatValue(v)} (${pct}%)`)
        } else if (type === "stacked") {
          payload.title = `Total ${this.formatValue(grand)}`
          payload.lines = (payload.lines || []).map((line, i) => ({ ...line, name: categories[i], value: this.valueText(values[0][i] ?? 0), share: this.shareText(values[0][i] ?? 0, grand) }))
          mark.setAttribute("aria-label", `${categories[c]}: ${this.valueText(v)}`)
        } else if (seriesForm) {
          payload.title = categories[c]
          payload.lines = (payload.lines || []).map((line, i) => {
            const next = { ...line, value: this.valueText(values[i]?.[c] ?? 0) }
            if (line.share !== undefined) next.share = this.shareText(values[i]?.[c] ?? 0, totals[c])
            return next
          })
          mark.setAttribute("aria-label", `${categories[c]}, ${this.seriesNames[s]}: ${this.valueText(v)}`)
        } else {
          payload.title = categories[c]
          payload.lines = (payload.lines || []).map((line) => {
            const next = { ...line, value: this.valueText(v) }
            if (line.share !== undefined) next.share = this.shareText(v, grand)
            return next
          })
          mark.setAttribute("aria-label", `${categories[c]}: ${this.valueText(v)}`)
        }
        mark.dataset.chartTooltip = JSON.stringify(payload)
      }
      const label = mark.querySelector("span")
      if (label && !RING_TYPES.includes(type)) label.textContent = this.formatValue(v)
    })

    this.element.querySelectorAll("[data-chart-column]").forEach((col) => {
      const c = Number(col.dataset.chartCategory)
      const payload = JSON.parse(col.dataset.chartTooltip || "{}")
      payload.title = categories[c]
      payload.lines = (payload.lines || []).map((line, i) => {
        const next = { ...line, value: this.valueText(values[i]?.[c] ?? 0) }
        if (line.share !== undefined) next.share = this.shareText(values[i]?.[c] ?? 0, totals[c])
        return next
      })
      col.dataset.chartTooltip = JSON.stringify(payload)
    })

    this.element.querySelectorAll("[data-chart-stack-total]").forEach((el) => {
      el.textContent = this.formatValue(totals[Number(el.dataset.chartStackTotal)] ?? 0)
    })

    // Ring legend: value and percent per slice; centre total.
    if (RING_TYPES.includes(type)) {
      this.element.querySelectorAll("[data-chart-legend] li").forEach((li, i) => {
        const spans = li.querySelectorAll("span")
        const v = values[0][i] ?? 0
        if (spans[2]) spans[2].textContent = this.formatValue(v)
        if (spans[3]) spans[3].textContent = `(${grand === 0 ? 0 : Math.round(Math.abs(v) / grand * 1000) / 10}%)`
      })
      const centre = this.element.querySelector("[data-chart-center-total]")
      if (centre && !centre.dataset.chartCenterFixed) centre.textContent = this.formatValue(grand)
    }

    // Hidden table: one row per category, one cell per series (or value, share).
    const rows = this.element.querySelectorAll(".sr-only tbody tr")
    rows.forEach((row, c) => {
      row.hidden = c >= categories.length
      if (row.hidden) return
      const cells = row.querySelectorAll("td")
      if (type === "stacked") {
        if (cells[1]) cells[1].textContent = this.valueText(values[0][c] ?? 0)
        if (cells[2]) cells[2].textContent = this.shareText(values[0][c] ?? 0, grand)
      } else if (RING_TYPES.includes(type)) {
        const v = values[0][c] ?? 0
        if (cells[1]) cells[1].textContent = `${this.formatValue(v)} (${grand === 0 ? 0 : Math.round(Math.abs(v) / grand * 1000) / 10}%)`
      } else {
        values.forEach((series, s) => { if (cells[s + 1]) cells[s + 1].textContent = this.valueText(series[c] ?? 0) })
      }
      if (cells[0]) cells[0].textContent = categories[c]
    })
  }

  // ---------------------------------------------------------------- line and area geometry

  get lineGeometry() {
    const svg = this.element.querySelector("svg[data-chart-left]")
    return svg ? geometry.lineGeometry(svg.dataset, this.categories.length) : null
  }

  linePath(values, geo) {
    return geometry.linePath(values, geo)
  }

  areaPath(values, geo) {
    return geometry.areaPath(values, geo)
  }

  currentLineValues() {
    return this.seriesNames.map((_, s) => this.categories.map((_, c) => this.valueFor(c, s)))
  }

  drawLine(values, geo) {
    values.forEach((series, s) => {
      const line = this.element.querySelector(`path[data-chart-path="line"][data-chart-series="${s}"]`)
      const area = this.element.querySelector(`path[data-chart-path="area"][data-chart-series="${s}"]`)
      if (line) line.setAttribute("d", this.linePath(series, geo))
      if (area) area.setAttribute("d", this.areaPath(series, geo))
      series.forEach((v, c) => {
        const point = this.markFor(c, s)
        if (point) {
          const at = geometry.plotPercent(geo.x(c), geo.y(v), geo)
          point.style.left = `${at.left}%`
          point.style.top = `${at.top}%`
        }
      })
    })
  }

  updateLine(values, animate, finish) {
    const geo = this.lineGeometry
    if (!geo) { finish(); return }

    // Grow the domain, up or down, if the new values need it; it never shrinks.
    const grown = geometry.expandDomain(geo.min, geo.max, values.flat())
    if (grown) {
      geo.min = grown.min
      geo.max = grown.max
      geo.range = geo.max - geo.min || 1
      const svg = this.element.querySelector("svg[data-chart-left]")
      svg.dataset.chartDomainMin = String(geo.min)
      svg.dataset.chartDomainMax = String(geo.max)
      this.redrawLineAxis(grown.ticks, geo)
    }

    const from = this.currentLineValues()
    if (!animate) {
      this.drawLine(values, geo)
      finish()
      return
    }
    this.runTween(UPDATE_MS, (t) => {
      const mix = from.map((series, s) => series.map((v, c) => v + ((values[s][c] ?? v) - v) * t))
      this.drawLine(mix, geo)
    }, finish)
  }

  // Grid lines stay in the SVG; tick labels are HTML in the left gutter (0.77.0).
  redrawLineAxis(ticks, geo) {
    const svg = this.element.querySelector("svg[data-chart-left]")
    const gutter = this.element.querySelector("[data-chart-line-ticks]")
    const tickProto = gutter?.querySelector("[data-chart-axis-tick]")
    const gridProto = svg.querySelector("line[data-chart-grid]")
    svg.querySelectorAll("line[data-chart-grid]").forEach((el) => el.remove())
    gutter?.querySelectorAll("[data-chart-axis-tick]").forEach((el) => el.remove())
    const first = svg.firstElementChild
    ticks.forEach((v) => {
      const y = geo.y(v)
      if (gridProto) {
        const g = gridProto.cloneNode(true)
        g.setAttribute("y1", y.toFixed(2))
        g.setAttribute("y2", y.toFixed(2))
        g.dataset.chartGrid = String(v)
        svg.insertBefore(g, first.nextSibling)
      }
      if (tickProto) {
        const t = tickProto.cloneNode(true)
        t.style.top = `${geometry.plotPercent(geo.left, y, geo).top}%`
        t.textContent = this.formatTick(v)
        t.dataset.chartAxisTick = String(v)
        gutter.append(t)
      }
    })
  }

  // ---------------------------------------------------------------- heatmap

  // Recolour every cell from the new values (null = no data), rewrite tooltips,
  // aria-labels, the scale bar and the hidden table. Shape must match the grid.
  updateHeat(series, columns) {
    const grid = this.element.querySelector("[data-chart-heat-grid]")
    if (!grid) return
    const values = series.map((s) => Array.from({ length: columns.length }, (_, c) => (s.values?.[c] === null || s.values?.[c] === undefined ? null : Number(s.values[c]))))
    const present = values.flat().filter((v) => v !== null)
    const min = present.length ? Math.min(...present) : 0
    const max = present.length ? Math.max(...present) : 0
    const color = this.markTargets[0] ? JSON.parse(this.markTargets[0].dataset.chartTooltip || "{}").lines?.[0]?.color : null
    const rows = this.seriesNames
    const trackStyle = "background-color: var(--rui-chart-track, light-dark(var(--color-zinc-100), var(--color-zinc-800)))"

    this.markTargets.forEach((cell) => {
      const r = Number(cell.dataset.chartSeries)
      const c = Number(cell.dataset.chartCategory)
      const v = values[r]?.[c] ?? null
      const payload = JSON.parse(cell.dataset.chartTooltip || "{}")
      payload.title = `${rows[r]} · ${columns[c]}`
      payload.lines = [{ ...(payload.lines?.[0] || {}), value: v === null ? "no data" : this.valueText(v) }]
      cell.dataset.chartTooltip = JSON.stringify(payload)
      cell.dataset.chartValue = String(v ?? 0)
      cell.setAttribute("aria-label", `${rows[r]}, ${columns[c]}: ${v === null ? "no data" : this.valueText(v)}`)
      if (v === null) {
        cell.dataset.chartNil = "true"
        cell.style.cssText = trackStyle
      } else {
        delete cell.dataset.chartNil
        cell.style.cssText = `background-color: color-mix(in srgb, ${color} ${geometry.heatPercent(v, min, max)}%, transparent)`
      }
    })

    const minEl = this.element.querySelector("[data-chart-heat-min]")
    const maxEl = this.element.querySelector("[data-chart-heat-max]")
    if (minEl) minEl.textContent = this.formatValue(min)
    if (maxEl) maxEl.textContent = this.formatValue(max)

    this.element.querySelectorAll(".sr-only tbody tr").forEach((row, r) => {
      const cells = row.querySelectorAll("td")
      columns.forEach((_, c) => {
        const v = values[r]?.[c] ?? null
        if (cells[c + 1]) cells[c + 1].textContent = v === null ? "no data" : this.valueText(v)
      })
    })
  }

  // ---------------------------------------------------------------- timeline strip

  // Only a period's group and end can change: { rows: [{ label, segments: [{ group?, to? }] }] },
  // segments matched by index, to: null means open. The bar's right edge and colour
  // transition; tooltip, aria-label, days and the hidden table are rewritten.
  updateStrip(detail) {
    const plot = this.element.querySelector("[data-chart-strip]")
    if (!plot || !Array.isArray(detail.rows)) return
    const domain = { from: strip.dayOf(plot.dataset.chartStripFrom), to: strip.dayOf(plot.dataset.chartStripTo), today: strip.dayOf(plot.dataset.chartStripToday) }
    const colors = JSON.parse(plot.dataset.chartStripColors || "[]")
    const groups = this.seriesNames
    const tableRows = this.element.querySelectorAll(".sr-only tbody tr")
    const motion = this.reducedMotion ? "none" : `width ${UPDATE_MS}ms ease-out, background-color ${UPDATE_MS}ms ease-out`

    detail.rows.forEach((row) => {
      const r = this.categories.indexOf(String(row.label))
      if (r < 0) return
      ;(row.segments || []).forEach((change, i) => {
        const mark = plot.querySelector(`[data-chart-category="${r}"][data-chart-strip-segment="${i}"]`)
        if (!mark) return
        const g = change.group === undefined ? Number(mark.dataset.chartSeries) : groups.indexOf(String(change.group))
        if (g < 0) return
        const from = strip.dayOf(mark.dataset.chartStripFrom)
        let to = mark.dataset.chartStripTo ? strip.dayOf(mark.dataset.chartStripTo) : null
        if (change.to !== undefined) to = change.to === null ? null : strip.dayOf(change.to)
        if (to !== null && to < from) return

        mark.style.transition = motion
        mark.style.width = `${strip.segmentBox(from, to, domain).width}%`
        mark.style.backgroundColor = colors[g]
        mark.dataset.chartSeries = String(g)
        if (to === null) {
          mark.dataset.chartStripOpen = "true"
          delete mark.dataset.chartStripTo
        } else {
          delete mark.dataset.chartStripOpen
          mark.dataset.chartStripTo = strip.isoOf(to)
        }
        const hatch = mark.querySelector("[data-chart-strip-hatch]")
        if (hatch) hatch.hidden = to !== null

        const days = strip.daysBetween(from, to ?? domain.today)
        const span = strip.spanText(from, to, domain.today)
        const payload = JSON.parse(mark.dataset.chartTooltip || "{}")
        const note = payload.lines?.[0]?.value || ""
        payload.lines = [{ name: groups[g], value: note, color: colors[g] }, { name: "", value: span }]
        mark.dataset.chartTooltip = JSON.stringify(payload)
        mark.dataset.chartValue = String(days)
        mark.setAttribute("aria-label", `${this.categories[r]}: ${[groups[g], note].filter(Boolean).join(", ")}, ${span}`)

        const cells = tableRows[Number(mark.dataset.chartStripTableRow)]?.querySelectorAll("td")
        if (cells?.length === 5) {
          cells[1].textContent = groups[g]
          cells[3].textContent = to === null ? "ongoing" : strip.formatDate(to)
          cells[4].textContent = strip.daysText(days).replace(/ days?$/, "")
        }
      })
    })
    this.element.dispatchEvent(new CustomEvent("rui-chart:updated", { bubbles: true, detail: { rows: detail.rows } }))
  }

  // ---------------------------------------------------------------- diagnostics

  // One named line per thing the markup must carry for this controller to work,
  // instead of a TypeError deep inside the first hover. Rendering still stands.
  preflight() {
    const missing = []
    const target = (name) => `a data-${this.identifier}-target="${name}" element`
    if (!this.element.dataset.chartCategories) missing.push("data-chart-categories on the figure")
    if (!this.element.dataset.chartSeriesNames) missing.push("data-chart-series-names on the figure")
    if (this.tooltipsValue && !this.hasTooltipTarget) missing.push(target("tooltip"))
    if (this.hasScrubberTarget && !this.hasPlayButtonTarget) missing.push(`${target("playButton")} for the timeline`)
    for (const m of missing) {
      ruiLog("chart", `missing ${m}; the markup predates this controller. Run bin/rails rui:update`, { element: this.element })
    }
    return missing.length === 0
  }

  // ---------------------------------------------------------------- sparkline

  // Redraw the sparkline from new values. The scale grows if the values need it
  // and never shrinks; the baseline stays where the server put it. No marks, so
  // no tween: a sparkline is too small for motion to read.
  updateSpark(values, categories) {
    const svg = this.element.querySelector("svg[data-chart-variant='sparkline']")
    if (!svg) return
    const W = 100
    const H = 24
    const pad = 2
    const baseline = svg.dataset.chartSparkBaseline === undefined ? null : Number(svg.dataset.chartSparkBaseline)
    let lo = Math.min(Number(svg.dataset.chartSparkLo), ...values, ...(baseline === null ? [] : [baseline]))
    let hi = Math.max(Number(svg.dataset.chartSparkHi), ...values, ...(baseline === null ? [] : [baseline]))
    if (hi === lo) hi = lo + 1
    svg.dataset.chartSparkLo = String(lo)
    svg.dataset.chartSparkHi = String(hi)
    const n = values.length
    const spark = geometry.sparkGeometry({ lo, hi, n, width: W, height: H, pad })
    const x = spark.x
    const y = spark.y

    const path = svg.querySelector("path[data-chart-path='spark']")
    if (path) path.setAttribute("d", values.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(3)} ${y(v).toFixed(3)}`).join(" "))

    const columns = svg.querySelectorAll("rect[data-chart-spark-column]")
    if (columns.length) {
      const slot = W / n
      const base = y(baseline === null ? lo : baseline)
      columns.forEach((rect, i) => {
        if (i >= n) { rect.setAttribute("height", "0"); return }
        const yy = y(values[i])
        rect.setAttribute("x", (i * slot + 0.5).toFixed(3))
        rect.setAttribute("width", Math.max(slot - 1, 0.5).toFixed(3))
        rect.setAttribute("y", Math.min(yy, base).toFixed(3))
        rect.setAttribute("height", Math.abs(yy - base).toFixed(3))
      })
    }

    const baseLine = svg.querySelector("line[data-chart-spark-baseline]")
    if (baseLine && baseline !== null) {
      baseLine.setAttribute("y1", y(baseline).toFixed(3))
      baseLine.setAttribute("y2", y(baseline).toFixed(3))
    }

    const dot = this.element.querySelector("[data-chart-spark-last]")
    if (dot && n > 0) {
      dot.style.left = `${(x(n - 1) * 100 / W).toFixed(3)}%`
      dot.style.top = `${(y(values[n - 1]) * 100 / H).toFixed(3)}%`
      dot.dataset.chartSparkLast = String(values[n - 1])
    }

    svg.setAttribute("aria-label", `${n} values, latest ${this.formatValue(values[n - 1] ?? 0)}`)
    const rows = this.element.querySelectorAll(".sr-only tbody tr")
    rows.forEach((row, i) => {
      row.hidden = i >= n
      const cells = row.querySelectorAll("td")
      if (cells[0]) cells[0].textContent = categories[i] ?? String(i + 1)
      if (cells[1]) cells[1].textContent = this.valueText(values[i] ?? 0)
    })
  }

  // ---------------------------------------------------------------- ring geometry

  arcPath(a, b, outer, inner, size) {
    return geometry.arcPath(a, b, outer, inner, size)
  }

  drawRing(values) {
    const svg = this.element.querySelector("svg[data-chart-outer]")
    if (!svg) return
    const outer = Number(svg.dataset.chartOuter)
    const inner = Number(svg.dataset.chartInner)
    const size = Number(svg.getAttribute("viewBox").split(" ")[2])
    const total = values.reduce((a, b) => a + Math.abs(b), 0)
    let acc = 0
    this.markTargets.forEach((slice, i) => {
      const frac = total === 0 ? 0 : Math.abs(values[i] ?? 0) / total
      slice.setAttribute("d", this.arcPath(acc, acc + frac, outer, inner, size))
      const mid = (acc + frac / 2) * 2 * Math.PI
      slice.dataset.chartDx = (4 * Math.sin(mid)).toFixed(2)
      slice.dataset.chartDy = (-4 * Math.cos(mid)).toFixed(2)
      acc += frac
    })
  }

  updateRing(values, animate, finish) {
    const from = this.markTargets.map((m) => Number(m.dataset.chartValue))
    if (!animate || from.length !== values.length) {
      this.drawRing(values)
      finish()
      return
    }
    this.runTween(UPDATE_MS, (t) => {
      this.drawRing(from.map((v, i) => v + (values[i] - v) * t))
    }, finish)
  }

  // ---------------------------------------------------------------- timeline (P1)

  togglePlay() {
    if (this.playing) {
      this.pause()
    } else {
      this.play()
    }
  }

  play() {
    if (!this.hasScrubberTarget) return
    this.settleEntry()
    const last = this.categories.length - 1
    if (this.timelineIndex >= last) this.revealUpTo(0, false)
    this.playing = true
    this.hasPlayButtonTarget && this.playButtonTarget.setAttribute("aria-pressed", "true")
    const step = this.reducedMotion ? Math.max(this.timelineStepValue, 300) : this.timelineStepValue
    this.timer = setInterval(() => {
      if (this.timelineIndex >= last) {
        this.pause()
        return
      }
      this.revealUpTo(this.timelineIndex + 1, true)
    }, step)
    this.dispatchTimeline()
  }

  pause() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    if (!this.playing) return
    this.playing = false
    this.hasPlayButtonTarget && this.playButtonTarget.setAttribute("aria-pressed", "false")
    this.dispatchTimeline()
  }

  scrub(event) {
    this.pause()
    this.settleEntry()
    this.revealUpTo(Number(event.currentTarget.value), !this.reducedMotion)
  }

  // Show categories 0..k. Columns beyond k collapse to the axis; the line and area
  // are clipped at x(k); the projection and band show only at the last step.
  revealUpTo(k, animate) {
    const last = this.categories.length - 1
    k = Math.max(0, Math.min(k, last))
    this.timelineIndex = k
    const motion = animate && !this.reducedMotion
    const ease = motion ? `transform ${this.timelineStepValue * 0.8}ms ease-out, opacity 300ms ease-out` : "none"

    if (LINE_TYPES.includes(this.element.dataset.chartType) && this.hasClipTarget) {
      const geo = this.lineGeometry
      this.cancelEntryWipe?.()
      const target = k === last ? geo.width : geo.x(k) + 6
      const from = Number(this.clipTarget.getAttribute("width"))
      this.cancelTween()
      if (motion) {
        this.runTween(this.timelineStepValue * 0.8, (t) => {
          this.clipTarget.setAttribute("width", (from + (target - from) * t).toFixed(2))
        })
      } else {
        this.clipTarget.setAttribute("width", String(target))
      }
      if (this.hasFinaleTarget) {
        this.finaleTarget.style.transition = ease
        this.finaleTarget.style.opacity = k === last ? "" : "0"
      }
      // Points and finale labels are HTML now, outside the clip: reveal by step.
      this.markTargets.forEach((point) => {
        point.style.visibility = Number(point.dataset.chartCategory) > k ? "hidden" : ""
      })
      this.element.querySelectorAll("[data-chart-finale-label]").forEach((el) => {
        el.style.transition = ease
        el.style.opacity = k === last ? "" : "0"
      })
    } else {
      this.markTargets.forEach((mark) => {
        const c = Number(mark.dataset.chartCategory)
        mark.style.transformOrigin = "bottom"
        mark.style.transition = ease
        mark.style.transform = c > k ? "scaleY(0)" : ""
      })
      this.element.querySelectorAll("[data-chart-stack-total], [data-chart-column]").forEach((el) => {
        const c = Number(el.dataset.chartStackTotal ?? el.dataset.chartCategory)
        el.style.transition = ease
        el.style.opacity = c > k ? "0" : ""
      })
    }

    if (this.hasScrubberTarget) this.scrubberTarget.value = String(k)
    if (this.hasTimelineLabelTarget) this.timelineLabelTarget.textContent = this.categories[k] ?? ""
    this.showCaption(k, last)
    this.showReadout(k, motion)
    this.dispatchTimeline()
  }

  // App-supplied captions only: the gem never writes one. At the end, the final
  // caption (or the last category's) shows.
  showCaption(k, last) {
    if (!this.hasCaptionTarget) return
    const captions = JSON.parse(this.element.dataset.chartCaptions || "[]")
    const final = this.element.dataset.chartCaptionFinal
    const text = k === last && final ? final : (captions[k] ?? "")
    this.captionTarget.textContent = text
  }

  // Count-up readout: tween the number from the previous step's total to this
  // step's, over 0.8 x step_ms. Reduced motion or a scrub jumps.
  showReadout(k, motion) {
    if (!this.hasReadoutValueTarget) return
    const target = this.readoutTotal(k)
    const from = this.readoutCurrent ?? target
    this.readoutCurrent = target
    if (!motion || from === target) {
      this.readoutValueTarget.textContent = this.formatValue(target)
      return
    }
    this.cancelReadoutTween?.()
    const decimals = fmt.decimalsOf(from, target)
    this.cancelReadoutTween = tween(this.timelineStepValue * 0.8, (t) => {
      this.readoutValueTarget.textContent = this.formatValue(fmt.snap(from + (target - from) * t, decimals))
    }, () => { this.readoutValueTarget.textContent = this.formatValue(target) })
  }

  // Total for the readout at step k: the picked series (or every series) by mode.
  // A series with no mark at k (a line's gap, the projection's category) reads the
  // projection value or its last real point, never 0 (shared/chart_readout.js).
  readoutTotal(k) {
    const picked = this.readoutSeriesValue >= 0 ? [this.readoutSeriesValue] : this.seriesNames.map((_, i) => i)
    const lookup = (c, s) => { const mark = this.markFor(c, s); return mark ? Number(mark.dataset.chartValue) : null }
    return readout.readoutTotal(lookup, k, picked, this.readoutModeValue, this.readoutProjection)
  }

  get readoutProjection() {
    const ds = this.element.dataset
    if (ds.chartProjectionCategory === undefined) return null
    return { category: Number(ds.chartProjectionCategory), value: Number(ds.chartProjectionValue) }
  }

  dispatchTimeline() {
    this.element.dispatchEvent(new CustomEvent("rui-chart:timeline", {
      bubbles: true,
      detail: { index: this.timelineIndex, label: this.categories[this.timelineIndex], playing: this.playing }
    }))
  }

  // ---------------------------------------------------------------- entry motion (P2)

  // Once, when the chart first scrolls into view. Bars grow from the axis, lines draw
  // in, areas fade up after the line, slices reveal in order, the projection and band
  // fade in last. Reduced motion skips all of it (final state is already rendered).
  startEntry() {
    const type = this.element.dataset.chartType
    const prepare = []
    const release = []
    const ease = (instant, spec) => (instant ? "none" : spec)

    if (GROW_IN_TYPES.includes(type)) {
      const axis = type === "bar" ? "scaleY" : "scaleX"
      const origin = axis === "scaleY" ? "bottom" : "left"
      this.markTargets.forEach((mark) => {
        prepare.push(() => {
          mark.style.transformOrigin = origin
          mark.style.transition = "none"
          mark.style.transform = `${axis}(0)`
        })
        release.push((instant) => {
          mark.style.transition = ease(instant, `transform ${MOTION_MS}ms ease-out`)
          mark.style.transform = ""
        })
      })
    } else if (LINE_TYPES.includes(type)) {
      // Lines draw in left to right by widening the plot clip. Strokes do not scale
      // with the stretched viewBox, so a dash-offset draw would not line up (0.77.0).
      const geo = this.lineGeometry
      if (this.hasClipTarget && geo) {
        const clip = this.clipTarget
        prepare.push(() => clip.setAttribute("width", String(geo.left)))
        release.push((instant) => {
          this.cancelEntryWipe?.()
          if (instant) {
            clip.setAttribute("width", String(geo.width))
            return
          }
          this.cancelEntryWipe = tween(MOTION_MS, (t) => {
            clip.setAttribute("width", (geo.left + (geo.width - geo.left) * t).toFixed(2))
          }, () => clip.setAttribute("width", String(geo.width)))
        })
      }
      this.element.querySelectorAll('path[data-chart-path="area"]').forEach((area) => {
        prepare.push(() => { area.style.transition = "none"; area.style.opacity = "0"; area.style.transform = "translateY(8px)" })
        release.push((instant) => {
          area.style.transition = ease(instant, `opacity ${MOTION_MS}ms ease-out ${MOTION_MS * 0.4}ms, transform ${MOTION_MS}ms ease-out ${MOTION_MS * 0.4}ms`)
          area.style.opacity = ""
          area.style.transform = ""
        })
      })
      this.markTargets.forEach((point, i) => {
        prepare.push(() => { point.style.transition = "none"; point.style.opacity = "0" })
        release.push((instant) => {
          point.style.transition = ease(instant, `opacity 200ms ease-out ${Math.min(i * 60, MOTION_MS)}ms`)
          point.style.opacity = point.dataset.chartBaseOpacity
        })
      })
      if (this.hasFinaleTarget) {
        const finale = this.finaleTarget
        prepare.push(() => { finale.style.transition = "none"; finale.style.opacity = "0" })
        release.push((instant) => {
          finale.style.transition = ease(instant, `opacity 400ms ease-out ${MOTION_MS}ms`)
          finale.style.opacity = ""
        })
      }
    } else if (type === "waffle") {
      // Squares fade in in fill order.
      this.element.querySelectorAll("[data-chart-waffle-square]").forEach((square, i) => {
        prepare.push(() => { square.style.transition = "none"; square.style.opacity = "0" })
        release.push((instant) => {
          square.style.transition = ease(instant, `opacity 200ms ease-out ${Math.min(i * 6, MOTION_MS)}ms`)
          square.style.opacity = ""
        })
      })
    } else if (type === "funnel") {
      // Steps grow from their centre, one after another.
      this.markTargets.forEach((bar, i) => {
        prepare.push(() => { bar.style.transition = "none"; bar.style.transform = "scaleX(0)" })
        release.push((instant) => {
          bar.style.transition = ease(instant, `transform ${MOTION_MS}ms ease-out ${i * 80}ms`)
          bar.style.transform = ""
        })
      })
    } else if (type === "lollipop") {
      // Rules grow from 0 in row order, each dot fades in as its rule lands.
      this.element.querySelectorAll("[data-chart-lollipop-rule]").forEach((rule, i) => {
        prepare.push(() => { rule.style.transition = "none"; rule.style.transform = "scaleX(0)" })
        release.push((instant) => {
          rule.style.transition = ease(instant, `transform ${MOTION_MS}ms ease-out ${i * 40}ms`)
          rule.style.transform = ""
        })
      })
      this.markTargets.forEach((dot, i) => {
        prepare.push(() => { dot.style.transition = "none"; dot.style.opacity = "0" })
        release.push((instant) => {
          dot.style.transition = ease(instant, `opacity 200ms ease-out ${i * 40 + MOTION_MS * 0.6}ms`)
          dot.style.opacity = dot.dataset.chartBaseOpacity
        })
      })
    } else if (type === "heatmap" || type === "calendar") {
      this.markTargets.forEach((cell) => {
        const r = Number(cell.dataset.chartSeries)
        prepare.push(() => { cell.style.transition = "none"; cell.style.opacity = "0" })
        release.push((instant) => {
          cell.style.transition = ease(instant, `opacity 300ms ease-out ${r * 40}ms`)
          cell.style.opacity = cell.dataset.chartBaseOpacity
        })
      })
    } else if (type === "timeline_strip") {
      // Bars grow from their start in row order; markers fade in once the bars are there.
      this.markTargets.forEach((mark) => {
        const delay = Number(mark.dataset.chartCategory) * 40
        const bar = mark.dataset.chartOrientation === "strip"
        prepare.push(() => {
          mark.style.transition = "none"
          if (bar) {
            mark.style.transformOrigin = "left"
            mark.style.transform = "scaleX(0)"
          } else {
            mark.style.opacity = "0"
          }
        })
        release.push((instant) => {
          mark.style.transition = ease(instant, bar ? `transform ${MOTION_MS}ms ease-out ${delay}ms` : `opacity 300ms ease-out ${delay + MOTION_MS}ms`)
          if (bar) mark.style.transform = ""
          else mark.style.opacity = mark.dataset.chartBaseOpacity
        })
      })
    } else if (RING_TYPES.includes(type)) {
      this.markTargets.forEach((slice, i) => {
        prepare.push(() => {
          slice.style.transformOrigin = "center"
          slice.style.transition = "none"
          slice.style.opacity = "0"
          slice.style.transform = "scale(0.9)"
        })
        release.push((instant) => {
          slice.style.transition = ease(instant, `opacity 300ms ease-out ${i * 40}ms, transform 300ms ease-out ${i * 40}ms`)
          slice.style.opacity = slice.dataset.chartBaseOpacity
          slice.style.transform = ""
          const settle = () => { slice.style.transition = "transform 200ms ease-out" }
          instant ? settle() : slice.addEventListener("transitionend", settle, { once: true })
        })
      })
    }

    if (prepare.length === 0) return
    prepare.forEach((fn) => fn())

    // Release once: on first intersection with motion, or instantly when something
    // else (an update, the timeline) needs the marks in their final state.
    this.entryRelease = (instant) => {
      if (!this.entryRelease) return
      this.entryRelease = null
      this.observer?.disconnect()
      release.forEach((fn) => fn(instant))
    }

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      requestAnimationFrame(() => this.entryRelease?.(false))
    }, { threshold: 0.2 })

    this.observer.observe(this.element)
  }

  // Put every mark in its final state right away if entry motion has not run yet.
  settleEntry() {
    this.entryRelease?.(true)
  }

  // ---------------------------------------------------------------- tween

  runTween(ms, frame, done) {
    this.cancelTween()
    this.stopTween = tween(ms, frame, () => {
      this.stopTween = null
      done && done()
    })
  }

  cancelTween() {
    this.stopTween?.()
    this.stopTween = null
  }

  // ---------------------------------------------------------------- dimming

  // The hovered category shows at full opacity, whatever its static state.
  dimOthers(category) {
    this.markTargets.forEach((mark) => {
      mark.style.opacity = mark.dataset.chartCategory === category ? "" : DIM
    })
    // Waffle squares are not marks (aria-hidden); they follow their legend row.
    this.element.querySelectorAll("[data-chart-waffle-square]").forEach((square) => {
      square.style.opacity = square.dataset.chartCategory === category ? "" : DIM
    })
  }

  restoreOpacity() {
    this.markTargets.forEach((mark) => { mark.style.opacity = mark.dataset.chartBaseOpacity })
    this.element.querySelectorAll("[data-chart-waffle-square]").forEach((square) => { square.style.opacity = "" })
  }

  // ---------------------------------------------------------------- helpers

  // SVG marks have no offsetParent, so ask for layout boxes instead.
  isVisible(el) {
    return el.getClientRects().length > 0
  }

  visibleMarks() {
    return this.markTargets.filter((mark) => !mark.hidden && this.isVisible(mark))
  }

  get categories() {
    return JSON.parse(this.element.dataset.chartCategories || "[]")
  }

  get seriesNames() {
    return JSON.parse(this.element.dataset.chartSeriesNames || "[]")
  }

  textNode(tag, text, className = "") {
    const el = document.createElement(tag)
    el.textContent = text
    if (className) el.className = className
    return el
  }

  get reducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  }
}
